import { useCreateEnterpriseSurveyAccount } from 'api/modules/accounts'
import { LoginResponse } from 'api/modules/authentication'
import cx from 'classnames'
import { screenSizes } from 'constants/global'
import { FormattedMessage } from 'locales/index'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { RouteComponentProps } from 'react-router-dom'
import { AppRegister, ComposedRegisterFieldName, registerHasErrors } from 'shared/components/common/RegisterForm'
import { Alert, Button, Form } from '@commutifi-fe/ui'
import { useCommutifiErrorMessages } from '@commutifi-fe/commutifi-specific/hooks'
import { RegisterFormPayload } from 'shared/interfaces/Account'
import { PublicPathsParams, PublicRouting } from 'src/routing'
import { authActions } from 'store/modules/auth'
import { wrapActionInPromise } from 'utils/helpers'
import { AuthLayout } from '../layout/AuthLayout'

const commonAlertProps = {
  style: { borderRadius: '4px' },
  className: 'u-margin-bottom--large'
}

export function SignUp({ match, history }: RouteComponentProps<{ [PublicPathsParams.Shortlink]: string }>) {
  const {
    error,
    isPending: isLoading,
    mutateAsync: createAccount
  } = useCreateEnterpriseSurveyAccount(match.params.shortlink)
  const { translate, getCommutifiErrorCode } = useCommutifiErrorMessages()
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })

  const dispatch = useDispatch()
  const login = React.useCallback(
    (...args: Parameters<typeof authActions.loginRequest<LoginResponse>>) =>
      dispatch(authActions.loginRequest(...args)),
    [dispatch]
  )

  const handleSubmit = async (values: RegisterFormPayload) => {
    try {
      const account = await createAccount(values)
      await wrapActionInPromise(
        login,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Should be defined
        { email: account.email, password: values.password! },
        { stayLoggedIn: true }
      )
    } finally {
      history.push(PublicRouting.root.path)
    }
  }
  const handleGoToLogin = () => history.push(`..${PublicRouting.auth.path}`)

  return (
    <AuthLayout
      title={<FormattedMessage id="signup.account.title" />}
      message={
        error ? <Alert {...commonAlertProps} type="error" message={translate(getCommutifiErrorCode(error))} /> : null
      }
    >
      <AppRegister
        onSubmit={handleSubmit}
        actions={
          <div className="u-flex-wrap u-flex-justify-end">
            <Button
              ghost
              onClick={handleGoToLogin}
              block={isXSmall}
              className={cx(isXSmall && 'u-margin-bottom--medium-2', !isXSmall && 'u-margin-right--medium-2')}
              style={{ whiteSpace: 'normal', wordBreak: 'break-word', alignSelf: 'end' }}
            >
              <FormattedMessage id="signup.account.buttons.alreadyHaveAnAccount" />
            </Button>
            <Form.Item noStyle shouldUpdate>
              {(formInstance) => (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={registerHasErrors(
                    formInstance,
                    new Set([ComposedRegisterFieldName.Email, ComposedRegisterFieldName.Password])
                  )}
                  block={isXSmall}
                >
                  <FormattedMessage id="signup.account.buttons.submit" />
                </Button>
              )}
            </Form.Item>
          </div>
        }
        className="u-margin-bottom--none"
      />
    </AuthLayout>
  )
}
